import { validateFileTypeRendability } from '../../../util/FileTypeSupportCheck';

// ── Raw DynamoDB types ──────────────────────────────────────────

interface DynamoString { S: string }
interface DynamoNumber { N: string }

interface RawInvitedUser {
  created?: DynamoString;
  group?: DynamoString;
  role?: DynamoString;
  id?: DynamoString;
  inviter?: DynamoString;
  expires?: DynamoString;
  email: DynamoString;
  project?: DynamoString;
  status: DynamoString;
}

interface RawProjectUser {
  user?: DynamoString;
  active?: DynamoNumber;
  role?: DynamoString;
  image?: DynamoString;
  firstname?: DynamoString;
  lastname?: DynamoString;
  email?: DynamoString;
  cognitoAttributes?: unknown;
}

interface RawResource {
  breadcrumbs?: unknown;
  created?: DynamoString;
  group?: DynamoString;
  id: DynamoString;
  itemtype?: DynamoString;
  mimeType?: DynamoString;
  name?: DynamoString;
  owner?: DynamoString;
  parent?: DynamoString;
  parentType?: DynamoString;
  preview?: DynamoNumber;
  searchField?: DynamoString;
  signedUrl?: DynamoString;
  size?: DynamoNumber;
  status?: DynamoNumber;
  thumbnailUrl?: DynamoString;
  snapshotUrl?: DynamoString;
  type?: DynamoString;
  key?: DynamoString;
  title?: DynamoString | DynamoNumber;
  previewImages?: unknown[];
  resources?: unknown[];
  [k: string]: unknown;
}

export interface RawTask {
  resource: DynamoString;
  created: DynamoString;
  index?: DynamoNumber;
  id: DynamoString;
  type: DynamoString;
  coord_x?: DynamoNumber;
  coord_y?: DynamoNumber;
  coords?: DynamoString;
  timestamp?: DynamoNumber;
  duration?: DynamoNumber;
  owner: DynamoString;
  tasktype?: DynamoString;
  message?: DynamoString;
  messages?: { L: Array<{ M: { created?: DynamoString; msg?: DynamoString; sender?: DynamoString } }> };
}

interface RawMessage {
  created?: DynamoString;
  msgbody?: DynamoString;
  sender?: DynamoString;
  msgid?: DynamoString;
}

export interface ProjectUser {
  uid: string | undefined;
  active: string | undefined;
  role: string | undefined;
  img: string | undefined;
  given_name: string | undefined;
  family_name: string | undefined;
  email: string | undefined;
}

export interface TransformedTask {
  resource: string;
  created: string;
  index: number;
  id: string;
  type: string;
  coord_x: string | undefined;
  coord_y: string | undefined;
  coords: [number, number][] | false;
  timestamp: string | undefined;
  duration: string | undefined;
  owner: string;
  taskType: string;
  message: string | undefined;
  messages: TransformedTaskMessage[];
  userData: ProjectUser | undefined;
  [k: string]: unknown;
}

export interface TransformedTaskMessage {
  created: string | undefined;
  content: string | undefined;
  sender: string | undefined;
  userData: ProjectUser | undefined;
}

export interface TransformedMessage {
  created: string | undefined;
  content: string | undefined;
  sender: string | undefined;
  id: string | undefined;
  userData: ProjectUser | undefined;
}

export interface TransformedResource {
  breadcrumbs: unknown;
  created: string | undefined;
  groupId: string | undefined;
  title: string | undefined;
  name: string | undefined;
  id: string;
  itemtype: string | undefined;
  mimeType: string | undefined;
  owner: string | undefined;
  parent: string | undefined;
  parentType: string | undefined;
  preview: boolean;
  searchField: string | undefined;
  signedUrl: string | undefined;
  size: string | undefined;
  status: string | undefined;
  thumbnailUrl: string | undefined;
  snapshotUrl: string | undefined;
  type: string | undefined;
  key: string | undefined;
  ext: string | undefined;
  isRendable: boolean;
  previewImages: unknown[];
  resources: unknown[];
}

// ── Transform functions ─────────────────────────────────────────

export function transformInvitedUsers(items: RawInvitedUser[]) {
  return items.map((u) => ({
    uid: u.id?.S,
    created: u.created?.S,
    role: u.role?.S,
    group: u.group?.S,
    inviter: u.inviter?.S,
    expires: u.expires?.S,
    email: u.email.S,
    status: u.status.S,
    project: u.project?.S,
  }));
}

export function transformProjectUsers(items: RawProjectUser[]): ProjectUser[] {
  return items.map((u) => ({
    uid: u.user?.S,
    active: u.active?.N,
    role: u.role?.S,
    img: u.image?.S,
    given_name: u.firstname?.S,
    family_name: u.lastname?.S,
    email: u.email?.S,
  }));
}

export function transformItems(items: RawResource[]): TransformedResource[] {
  return items.map(transformResource);
}

export function transformResource(item: RawResource): TransformedResource {
  const ext = item.key?.S?.substring(item.key.S.lastIndexOf('.') + 1);

  // Already transformed items pass through
  if (!item.id.S) return item as unknown as TransformedResource;

  return {
    breadcrumbs: item.breadcrumbs,
    created: item.created?.S,
    groupId: item.group?.S,
    title: (item.title as DynamoString)?.S || (item.title as DynamoNumber)?.N || item.name?.S,
    name: item.name?.S || (item.title as DynamoString)?.S,
    id: item.id.S,
    itemtype: item.itemtype?.S,
    mimeType: item.mimeType?.S,
    owner: item.owner?.S,
    parent: item.parent?.S,
    parentType: item.parentType?.S,
    preview: item.preview?.N === '1',
    searchField: item.searchField?.S,
    signedUrl: item.signedUrl?.S,
    size: item.size?.N,
    status: item.status?.N,
    thumbnailUrl: item.thumbnailUrl?.S || item.snapshotUrl?.S,
    snapshotUrl: item.thumbnailUrl?.S || item.snapshotUrl?.S,
    type: item.type?.S,
    key: item.key?.S,
    ext,
    isRendable: validateFileTypeRendability(item.type?.S, ext),
    previewImages: (item.previewImages as unknown[]) || [],
    resources: (item.resources as unknown[]) || [],
  };
}

function transformTaskMessages(messages: RawTask['messages'] extends { L: infer T } ? T : never = [], users: ProjectUser[]): TransformedTaskMessage[] {
  return messages.map(({ M }) => {
    const userData = users.find((i) => i.uid === M.sender?.S);
    return {
      created: M.created?.S,
      content: M.msg?.S,
      sender: M.sender?.S,
      userData,
    };
  });
}

export function transformTask(task: RawTask, users: ProjectUser[]): TransformedTask {
  const userData = users.find((i) => i.uid === task.owner?.S);
  return {
    resource: task.resource.S,
    created: task.created.S,
    index: parseInt(task.index?.N || '1'),
    id: task.id.S,
    type: task.type.S,
    coord_x: task.coord_x?.N,
    coord_y: task.coord_y?.N,
    coords: !!task.coords?.S && JSON.parse(task.coords.S),
    timestamp: task.timestamp?.N,
    duration: task.duration?.N,
    owner: task.owner.S,
    taskType: task.tasktype?.S || 'default',
    message: task.message?.S,
    messages: transformTaskMessages(task.messages?.L, users),
    userData,
  };
}

export function transformTasks(items: RawTask[], users: ProjectUser[]): TransformedTask[] {
  return items
    .map((task) => transformTask(task, users))
    .sort((i, j) => new Date(i.created).getTime() - new Date(j.created).getTime());
}

export function transformMessages(messages: RawMessage[] = [], users: ProjectUser[]): TransformedMessage[] {
  return messages
    .map((m) => {
      const userData = users.find((i) => i.uid === m.sender?.S);
      return {
        created: m.created?.S,
        content: m.msgbody?.S,
        sender: m.sender?.S,
        id: m.msgid?.S,
        userData,
      };
    })
    .sort((i, j) => new Date(i.created ?? 0).getTime() - new Date(j.created ?? 0).getTime());
}
